import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h1 style={styles.title}>Privacy Policy for LinkPerks</h1>
        <p style={styles.lastUpdated}>Last Updated: March 1st, 2026</p>
        
        <p style={styles.intro}>
          LinkWell Insurance Group Inc. ("we," "us," or "our") operates the LinkPerks mobile application and platform (the "Service"). We are committed to protecting your privacy and safeguarding your personal information.
        </p>
        
        <p style={styles.intro}>
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use LinkPerks to access exclusive offers, savings, and rewards.
        </p>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Information We Collect</h2>
          <p style={styles.paragraph}>
            We collect information that identifies you ("Personal Information") and information regarding your use of the Service to verify your eligibility for perks and provide you with relevant local offers.
          </p>
          
          <h3 style={styles.subTitle}>A. Information You Provide to Us</h3>
          <ul style={styles.list}>
            <li><strong>Account Registration:</strong> Name, email address, password, and company name.</li>
            <li><strong>Eligibility Verification:</strong> Information verifying your employment or membership with a participating organization (e.g., employee ID, company email, or unique access code).</li>
            <li><strong>Profile Information:</strong> Preferences you set regarding the types of perks or categories you are interested in (e.g., "Health & Wellness," "Dining").</li>
            <li><strong>Redemption Data:</strong> Information provided when you redeem a specific perk (e.g., claiming a coupon code).</li>
          </ul>

          <h3 style={styles.subTitle}>B. Information Collected Automatically</h3>
          <ul style={styles.list}>
            <li><strong>Location Data:</strong> With your permission, we collect precise geolocation data to show you perks and vendors near you. You can disable this in your device settings, though some features may not function correctly.</li>
            <li><strong>Device Information:</strong> Device model, operating system, unique device identifiers, and IP address.</li>
            <li><strong>Usage Data:</strong> Details on how you use the app, such as which offers you view, save, or redeem, and the time and duration of your sessions.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. How We Use Your Information</h2>
          <p style={styles.paragraph}>We use the data we collect to:</p>
          <ul style={styles.list}>
            <li><strong>Provide the Service:</strong> Create your account, verify your eligibility for exclusive perks, and process your redemptions.</li>
            <li><strong>Personalize Experience:</strong> Recommend offers and vendors based on your location and past usage.</li>
            <li><strong>Vendor Connection:</strong> Facilitate the redemption of perks with our third-party vendor partners.</li>
            <li><strong>Communications:</strong> Send you administrative updates, technical notices, and (if you have opted in) newsletters or notifications about new exclusive offers.</li>
            <li><strong>Analytics:</strong> Monitor and analyze usage trends to improve the functionality and user experience of LinkPerks.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Sharing of Your Information</h2>
          <p style={styles.paragraph}>
            We do not sell your personal information to third parties. We typically share information only in the following situations:
          </p>
          <ul style={styles.list}>
            <li><strong>With Vendors (Perk Providers):</strong> When you redeem a specific offer, we may share necessary verification data (such as a redemption code or membership status) with the vendor to validate the transaction. We do not share your full profile with vendors for their independent marketing purposes without your explicit consent.</li>
            <li><strong>Service Providers:</strong> We may share data with third-party vendors who perform services on our behalf, such as hosting (e.g., AWS/Azure), email delivery, and customer support tools.</li>
            <li><strong>Business Transfers:</strong> If LinkWell Insurance Group Inc. is involved in a merger, acquisition, or asset sale, your information may be transferred.</li>
            <li><strong>Legal Requirements:</strong> We may disclose information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Data Retention</h2>
          <p style={styles.paragraph}>
            We retain your personal information only for as long as necessary to fulfill the purposes set out in this policy.
          </p>
          <ul style={styles.list}>
            <li><strong>Account Data:</strong> Retained as long as your account is active.</li>
            <li><strong>Usage Logs:</strong> Retained for a limited period for analytics and security purposes, then anonymized or deleted.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Security of Your Information</h2>
          <p style={styles.paragraph}>
            We use administrative, technical, and physical security measures to help protect your personal information. This includes encryption of data in transit and restricted access to personal data by our employees. However, no electronic transmission over the internet is 100% secure, so we cannot guarantee absolute security.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. International Data Transfers</h2>
          <p style={styles.paragraph}>
            While LinkWell is based in Canada, some of our service providers may process or store data outside of Canada (e.g., in the United States). As a result, your information may be subject to the laws of those jurisdictions.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Your Rights</h2>
          <p style={styles.paragraph}>Under Canadian privacy laws, you have the right to:</p>
          <ul style={styles.list}>
            <li><strong>Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information.</li>
            <li><strong>Deletion:</strong> Request deletion of your account and personal data (subject to legal retention requirements).</li>
            <li><strong>Withdraw Consent:</strong> You may withdraw consent for location tracking or marketing communications at any time via the app settings.</li>
          </ul>
          <p style={styles.paragraph}>To exercise these rights, please contact us at the details below.</p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Third-Party Links</h2>
          <p style={styles.paragraph}>
            LinkPerks may contain links to third-party websites or applications (e.g., a vendor's website to claim a deal). We are not responsible for the privacy practices of those third parties. We encourage you to review their privacy policies before providing them with any personal data.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Contact Us</h2>
          <p style={styles.paragraph}>
            If you have questions about this Privacy Policy or our data practices, please contact our Privacy Officer:
          </p>
          <div style={styles.contactInfo}>
            <p style={styles.contactLine}><strong>LinkWell Insurance Group Inc.</strong></p>
            <p style={styles.contactLine}>Email: <a href="mailto:curtis.metzler@linkwell.ca" style={styles.link}>curtis.metzler@linkwell.ca</a></p>
            <p style={styles.contactLine}>Phone: 204-881-2591 (Manitoba)</p>
            <p style={styles.contactLine}>Mailing Address: 145 Brittany Dr, Winnipeg, MB R3R 3T1</p>
          </div>
        </section>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '40px 20px',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#0A0E29',
    marginBottom: '8px',
  },
  lastUpdated: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px',
    fontStyle: 'italic',
  },
  intro: {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#374151',
    marginBottom: '16px',
  },
  section: {
    marginTop: '32px',
  },
  sectionTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#0A0E29',
    marginBottom: '16px',
  },
  subTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    marginTop: '20px',
    marginBottom: '12px',
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#374151',
    marginBottom: '12px',
  },
  list: {
    paddingLeft: '24px',
    marginBottom: '16px',
  },
  contactInfo: {
    backgroundColor: '#f3f4f6',
    padding: '20px',
    borderRadius: '8px',
    marginTop: '16px',
  },
  contactLine: {
    fontSize: '15px',
    color: '#374151',
    marginBottom: '8px',
    margin: '0 0 8px 0',
  },
  link: {
    color: '#00B1B0',
    textDecoration: 'none',
  },
};

export default PrivacyPolicyPage;
